import React, { useMemo, useState } from 'react';
import { Segmented, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/Store';
import {
  NUTRIENT_BALANCE_NO_REMOVAL_MESSAGE,
  NUTRIENT_BALANCE_PARTIAL_REMOVAL_MESSAGE,
} from 'constants/messageConstants';
import {
  AREA_UNIT,
  NUTRIENT_BALANCE_BALANCE_HINT,
  NUTRIENT_BALANCE_BALANCE_LABEL,
  NUTRIENT_BALANCE_REMOVED_LABEL,
  NUTRIENT_BALANCE_SHORTFALL_LABEL,
  NUTRIENT_BALANCE_SURPLUS_LABEL,
  NUTRIENT_BALANCE_VIEW_BALANCE_LABEL,
  NUTRIENT_BALANCE_VIEW_TALLY_LABEL,
  RIGHT_PANEL_TAB_NUTRITION,
  RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX,
} from 'constants/uiConstants';
import { useScopedInputs } from 'hooks/useScopedInputs';
import { useFetchGUnits } from 'hooks/useFetchGUnits';
import { useElementWidth } from 'hooks/common/useElementWidth';
import { InputTaskCategory } from 'interfaces/IInputTask';
import {
  computeNutritionPerHa,
  NUTRIENT_DISPLAY_ORDER,
  NUTRIENT_LABELS,
  NUTRIENT_PIE_COLORS,
  type NutritionPerHaResult,
  type NutritionTally,
  type PartPaddockApplication,
} from 'pages/FarmsPage/Workspace/Insights/computeLiveTotals';
import { computeCropRemovalPerHa } from 'pages/FarmsPage/Workspace/Insights/computeCropRemoval';
import {
  ACTUAL_ONLY_CATEGORIES,
  PLAN_ONLY_CATEGORIES,
  scaleForDisplay,
} from 'pages/FarmsPage/Workspace/Insights/insightUtils';
import { NUTRIENT_REMOVAL_KEY } from 'pages/FarmsPage/Workspace/Insights/nutrition/computeNutritionAudit';
import NutritionProgressChart, {
  type NutrientProgressItem,
} from './NutritionProgressChart';
import { ChartEmptyState } from './PlanActualChartControls';

/**
 * A nutrient row's shared display unit — Plan and Actual are always shown in
 * the same unit (kg or t) so they stay directly comparable within a row; the
 * unit itself is shown once next to the nutrient's own label rather than
 * repeated on every value cell. Picked from whichever of Plan/Actual is
 * larger, so a row that's crossed the tonne threshold shows both figures in
 * tonnes even if one of them individually wouldn't have.
 */
const resolveRowUnit = (...values: number[]): string =>
  scaleForDisplay(Math.max(...values, 0), 'kg').unit ?? 'kg';

const scaleToUnit = (value: number, unit: string): number =>
  unit === 't' ? value / 1000 : value;

/** One nutrient's rate, formatted for display in the row's shared unit. isOverPlan flags an Actual value that exceeds (or has no) Plan rate — see the chart's own over-plan treatment for the same reasoning. */
const NutrientValue: React.FC<{
  tally: NutritionTally | null;
  nutrientKey: keyof NutritionTally;
  unit: string;
  isOverPlan?: boolean;
}> = ({ tally, nutrientKey, unit, isOverPlan }) => {
  const raw = tally?.[nutrientKey] ?? 0;
  if (raw <= 0) return <span className="text-gray-300">—</span>;
  const value = scaleToUnit(raw, unit);
  return (
    <span className={isOverPlan ? 'font-semibold text-danger' : undefined}>
      {value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
    </span>
  );
};

type NutrientView = 'balance' | 'tally';

/**
 * Applied minus removed (OP-2563). The sign says surplus or shortfall, with
 * the word for screen readers, so colour is never the only signal. A dash
 * when there's no removal to compare with.
 */
const BalanceValue: React.FC<{
  applied: number;
  removed: number | null;
  unit: string;
}> = ({ applied, removed, unit }) => {
  if (removed === null) return <span className="text-gray-muted">—</span>;
  const value = scaleToUnit(applied - removed, unit);
  // Rounded to the precision shown, so noise around zero reads as balanced.
  const rounded = Math.round(value * 100) / 100;
  if (rounded === 0) return <span>0</span>;
  const isSurplus = rounded > 0;
  return (
    <span className={isSurplus ? 'text-success' : 'text-danger-dark'}>
      {isSurplus ? '+' : '−'}
      {Math.abs(rounded).toLocaleString(undefined, { maximumFractionDigits: 2 })}
      <span className="sr-only">
        {' '}
        {isSurplus ? NUTRIENT_BALANCE_SURPLUS_LABEL : NUTRIENT_BALANCE_SHORTFALL_LABEL}
      </span>
    </span>
  );
};

const formatNumber = (value: number): string =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatApplication = (app: PartPaddockApplication): string => {
  const rate = app.rateUnit
    ? `${formatNumber(app.rate)} ${app.rateUnit}/${AREA_UNIT}`
    : formatNumber(app.rate);
  return `${app.productName}: ${rate} on ${formatNumber(app.areaHa)} ${AREA_UNIT}`;
};

/**
 * Explains the area the rates are averaged over (OP-2386). Only rendered when
 * that isn't obvious: a contributing step covered part of the paddock
 * (including variable-rate zones), or the paddock area differs between plans.
 * Plan and Actual share the same plans and divisor, so either result's plans
 * describe both.
 */
const AveragedAreaNote: React.FC<{
  plan: NutritionPerHaResult;
  actual: NutritionPerHaResult;
}> = ({ plan, actual }) => {
  const areasDiffer = plan.plans.some(
    (p) => formatNumber(p.areaHa) !== formatNumber(plan.plans[0].areaHa)
  );
  const groups = [
    { label: 'Plan', apps: plan.partPaddockApplications },
    { label: 'Actual', apps: actual.partPaddockApplications },
  ].filter((g) => g.apps.length > 0);

  if (!areasDiffer && groups.length === 0) return null;

  const title = (
    <div className="flex flex-col gap-1">
      <p>
        {areasDiffer
          ? `Paddock area differs between plans: ${plan.plans
              .map((p) => `${p.label} ${formatNumber(p.areaHa)} ${AREA_UNIT}`)
              .join(', ')}. Each plan's rate uses its own area.`
          : `Averaged over ${formatNumber(plan.areaHa)} ${AREA_UNIT}.`}
        {groups.length > 0 &&
          ' Includes applications to part of the paddock, including variable-rate zones:'}
      </p>
      {groups.map((g) => (
        <div key={g.label}>
          <p className="font-semibold">{g.label}</p>
          <ul className="list-disc pl-4">
            {g.apps.map((app, idx) => (
              <li key={`${app.inputId}-${idx}`}>{formatApplication(app)}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip title={title} placement="left">
      <InfoCircleOutlined
        aria-label="How nutrient rates are averaged"
        tabIndex={0}
        className="cursor-help text-gray-muted"
      />
    </Tooltip>
  );
};

/**
 * Displays a compact tally of planned nutrient rates (kg/ha) across all
 * SoilInput steps within the current tree selection scope.
 *
 * Shows Plan and Actual side by side when the panel is wide enough; below
 * RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX it collapses to a single column driven
 * by the shared Plan/Actual control at the top of the Right Panel (OP-2333 —
 * one control for every section, not one per panel). The chart shows both
 * at once per nutrient — Actual applied so far against that nutrient's own
 * Plan target (OP-2333; previously a composition-% donut with a Plan/Actual
 * toggle, see NutritionProgressChart for the current design).
 *
 * Only nutrients with a non-zero rate (in either category) are shown.
 * Rates ≥ 1000 kg/ha are auto-scaled to t/ha using scaleForDisplay.
 */
const NutritionTallyPanel: React.FC = () => {
  const { scopedInputs, isLoading: isInputsLoading } = useScopedInputs();
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const isWide = width === 0 || width >= RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX;
  const narrowCategory = useSelector(
    (state: RootState) => state.insightsReducer.rightPanel.categoryFilter
  );

  const activeGOrganisation = useSelector(
    (state: RootState) => state.globalCatalogueReducer.activeGOrganisation
  );
  const chartVisible = useSelector(
    (state: RootState) =>
      state.insightsReducer.rightPanel.chartVisible[RIGHT_PANEL_TAB_NUTRITION]
  );
  const { data: gUnits = [], isLoading: isGUnitsLoading } = useFetchGUnits(
    activeGOrganisation || null
  );

  const isLoading = isInputsLoading || isGUnitsLoading;

  const [hoveredNutrient, setHoveredNutrient] = useState<string | null>(null);
  // Plan vs Actual, or applied against crop removal — one at a time, so the
  // table never squeezes four figures into the panel's width.
  const [view, setView] = useState<NutrientView>('balance');

  const planResult = useMemo(
    () =>
      isLoading
        ? null
        : computeNutritionPerHa(scopedInputs, gUnits, PLAN_ONLY_CATEGORIES),
    [scopedInputs, gUnits, isLoading]
  );
  const actualResult = useMemo(
    () =>
      isLoading
        ? null
        : computeNutritionPerHa(scopedInputs, gUnits, ACTUAL_ONLY_CATEGORIES),
    [scopedInputs, gUnits, isLoading]
  );
  const planPerHa = planResult?.perHa ?? null;
  const actualPerHa = actualResult?.perHa ?? null;
  // Crop removal from the plans' production at the Expected yield — the
  // figure the Insights Nutrition lens shows for the same plans (OP-2563).
  const removal = useMemo(
    () =>
      isLoading
        ? null
        : computeCropRemovalPerHa(scopedInputs, gUnits, PLAN_ONLY_CATEGORIES),
    [scopedInputs, gUnits, isLoading]
  );
  const hasRemoval = (removal?.planCount ?? 0) > 0;
  /** kg/ha removed, or null where it can't be compared (no removal, or a nutrient crops don't record). */
  const removedFor = (key: keyof NutritionTally): number | null =>
    hasRemoval && NUTRIENT_REMOVAL_KEY[key] ? (removal?.removed[key] ?? 0) : null;

  if (isLoading) {
    return (
      <div ref={ref} className="flex items-center justify-center py-8">
        <span className="text-base text-gray-muted">Loading...</span>
      </div>
    );
  }

  const activeRows = NUTRIENT_DISPLAY_ORDER.filter(
    (key) =>
      (planPerHa?.[key] ?? 0) > 0 ||
      (actualPerHa?.[key] ?? 0) > 0 ||
      (removedFor(key) ?? 0) > 0
  );
  const removalNote =
    removal && removal.missingPlanCount > 0
      ? hasRemoval
        ? NUTRIENT_BALANCE_PARTIAL_REMOVAL_MESSAGE(removal.missingPlanCount)
        : NUTRIENT_BALANCE_NO_REMOVAL_MESSAGE
      : null;

  if (activeRows.length === 0) {
    return (
      <div ref={ref} className="flex flex-col items-center gap-2 py-8 text-center">
        <span className="text-base text-gray-muted">No nutrient data</span>
        {removalNote && (
          <span className="text-sm text-gray-dark">{removalNote}</span>
        )}
      </div>
    );
  }

  const narrowTally = narrowCategory === InputTaskCategory.Plan ? planPerHa : actualPerHa;
  // Balance compares Plan with removal when Plan and Actual sit side by side;
  // narrow, it follows the shared Plan/Actual control.
  const balanceTally = isWide ? planPerHa : narrowTally;
  const balanceBasisLabel =
    !isWide && narrowCategory === InputTaskCategory.Actual ? 'Actual' : 'Plan';
  const isOverPlan = (key: keyof NutritionTally): boolean => {
    const plan = planPerHa?.[key] ?? 0;
    const actual = actualPerHa?.[key] ?? 0;
    return actual > 0 && (plan <= 0 || actual > plan);
  };

  const progressItems: NutrientProgressItem[] = activeRows.map((key) => ({
    key,
    plan: planPerHa?.[key] ?? 0,
    actual: actualPerHa?.[key] ?? 0,
  }));

  const nutrientHeader = (
    <th className="pb-1.5 pr-3 text-left text-sm font-semibold text-gray-muted">
      <span className="inline-flex items-center gap-1.5">
        Nutrient
        {planResult && actualResult && (
          <AveragedAreaNote plan={planResult} actual={actualResult} />
        )}
      </span>
    </th>
  );
  const valueHeaderClass =
    'pb-1.5 pl-2 text-right text-sm font-semibold text-gray-muted';

  const renderNutrientCell = (key: keyof NutritionTally, unit: string) => (
    <td className="py-1 pr-3 text-label text-charcoal">
      <span className="inline-flex items-center gap-1.5">
        <svg width="8" height="8" aria-hidden="true" className="shrink-0">
          <circle
            cx="4"
            cy="4"
            r="3.5"
            fill={NUTRIENT_PIE_COLORS[key] ?? '#9ca3af'}
          />
        </svg>
        {NUTRIENT_LABELS[key]}
        <span className="text-xs font-semibold text-gray-muted">
          ({unit}/ha)
        </span>
      </span>
    </td>
  );

  const rowProps = (key: keyof NutritionTally, idx: number) => ({
    key,
    onMouseEnter: () => setHoveredNutrient(key),
    onMouseLeave: () => setHoveredNutrient(null),
    className: `hover:bg-rowHover ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`,
  });

  return (
    <div ref={ref} className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Segmented
          size="small"
          value={view}
          onChange={(value) => setView(value as NutrientView)}
          options={[
            { label: NUTRIENT_BALANCE_VIEW_BALANCE_LABEL, value: 'balance' },
            { label: NUTRIENT_BALANCE_VIEW_TALLY_LABEL, value: 'tally' },
          ]}
        />
      </div>
      {view === 'balance' ? (
        <table className="w-full text-base">
          <thead>
            <tr className="border-b">
              {nutrientHeader}
              <th className={valueHeaderClass}>{balanceBasisLabel}</th>
              <th className={valueHeaderClass}>
                {NUTRIENT_BALANCE_REMOVED_LABEL}
              </th>
              <th className={valueHeaderClass}>
                <Tooltip title={NUTRIENT_BALANCE_BALANCE_HINT(balanceBasisLabel)}>
                  <span className="cursor-help">
                    {NUTRIENT_BALANCE_BALANCE_LABEL}
                  </span>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {activeRows.map((key, idx) => {
              const removed = removedFor(key);
              const applied = balanceTally?.[key] ?? 0;
              const unit = resolveRowUnit(applied, removed ?? 0);
              const { key: rowKey, ...row } = rowProps(key, idx);
              return (
                <tr key={rowKey} {...row}>
                  {renderNutrientCell(key, unit)}
                  <td className="py-1 pl-2 text-right tabular-nums text-charcoal">
                    <NutrientValue tally={balanceTally} nutrientKey={key} unit={unit} />
                  </td>
                  <td className="py-1 pl-2 text-right tabular-nums text-charcoal">
                    {removed === null || removed <= 0 ? (
                      <span className="text-gray-muted">—</span>
                    ) : (
                      formatNumber(scaleToUnit(removed, unit))
                    )}
                  </td>
                  <td className="py-1 pl-2 text-right tabular-nums">
                    <BalanceValue applied={applied} removed={removed} unit={unit} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <table className="w-full text-base">
          <thead>
            <tr className="border-b">
              {nutrientHeader}
              {isWide ? (
                <>
                  <th className={valueHeaderClass}>Plan</th>
                  <th className={valueHeaderClass}>Actual</th>
                </>
              ) : (
                <th className={valueHeaderClass}>
                  {narrowCategory === InputTaskCategory.Plan ? 'Plan' : 'Actual'}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {activeRows.map((key, idx) => {
              const unit = resolveRowUnit(
                planPerHa?.[key] ?? 0,
                actualPerHa?.[key] ?? 0
              );
              const { key: rowKey, ...row } = rowProps(key, idx);
              return (
                <tr key={rowKey} {...row}>
                  {renderNutrientCell(key, unit)}
                  {isWide ? (
                    <>
                      <td className="py-1 pl-2 text-right tabular-nums text-charcoal">
                        <NutrientValue tally={planPerHa} nutrientKey={key} unit={unit} />
                      </td>
                      <td className="py-1 pl-2 text-right tabular-nums text-charcoal">
                        <NutrientValue
                          tally={actualPerHa}
                          nutrientKey={key}
                          unit={unit}
                          isOverPlan={isOverPlan(key)}
                        />
                      </td>
                    </>
                  ) : (
                    <td className="py-1 pl-2 text-right tabular-nums text-charcoal">
                      <NutrientValue
                        tally={narrowTally}
                        nutrientKey={key}
                        unit={unit}
                        isOverPlan={
                          narrowCategory === InputTaskCategory.Actual && isOverPlan(key)
                        }
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {view === 'balance' && removalNote && (
        <p className="text-sm text-gray-dark">{removalNote}</p>
      )}

      {chartVisible &&
        (progressItems.length > 0 ? (
          <NutritionProgressChart
            items={progressItems}
            hoveredKey={hoveredNutrient}
            onHoverKeyChange={setHoveredNutrient}
          />
        ) : (
          <ChartEmptyState />
        ))}
    </div>
  );
};

export default NutritionTallyPanel;
